import {
  Landmark, Building2, FlaskConical, Factory, Shield,
  Eye, RefreshCcw, Bot, Activity, AlertTriangle, BookOpen, LineChart, Workflow, Lock,
} from 'lucide-react';

const IMG = {
  bfsi:         'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&q=80',
  enterprise:   'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1600&q=80',
  pharma:       'https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?w=1600&q=80',
  manufacturing:'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=1600&q=80',
  government:   'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1600&q=80',
};

const PILLAR = {
  observability: { icon: Eye,         label: 'Observability' },
  selfHealing:   { icon: RefreshCcw,  label: 'Self-Healing'  },
  agents:        { icon: Bot,         label: 'AI & Agents'   },
};

export const INDUSTRIES = {
  bfsi: {
    slug: 'bfsi', icon: Landmark, image: IMG.bfsi,
    title: ['Operations that customers,', 'and regulators, can rely on.'],
    lede:  'Banking, financial services, and insurance run on uptime and trust. MetaxuOne unifies observability, self-healing, and AI agents into a single operating layer for the systems that customers — and regulators — depend on.',
    stats: [
      { k: 'Regulator-ready', v: 'RBI · DORA · SOC 2 · ISO 27001' },
      { k: 'MTTR target',     v: '< 5 minutes for tier-1' },
      { k: 'Customer impact', v: 'Measured, not guessed' },
    ],
    pillars: {
      observability: { body: 'See every core-banking transaction, payment flow, and channel session as it happens — and correlate them to customer-facing KPIs in real time.', points: ['Real-time payment-flow visibility','Channel & API observability','Regulatory reporting from live data'] },
      selfHealing:   { body: 'Auto-detect and remediate failed transactions, stuck batch jobs, and channel anomalies before they reach the customer or the regulator.', points: ['Auto-retry stuck payments','Channel auto-failover','MTTR ↓ on tier-1 incidents'] },
      agents:        { body: 'Audit-grade AI agents support fraud triage, KYC investigations, and operations co-pilots — fully logged, policy-bound, and explainable.', points: ['Fraud triage co-pilot','Audit-grade decision lineage','Human approval rails'] },
    },
    useCases: [
      { icon: AlertTriangle, title: 'Payment ops resilience', text: 'Detect, isolate, and recover failed payment flows in minutes.' },
      { icon: Lock,          title: 'Regulator-ready posture', text: 'Continuous control monitoring for RBI, DORA, SOC 2.' },
      { icon: Workflow,      title: 'Fraud & AML triage',      text: 'AI agents that explain themselves and never act unsupervised.' },
    ],
  },

  enterprise: {
    slug: 'enterprise', icon: Building2, image: IMG.enterprise,
    title: ['One operating layer.', 'Every domain.'],
    lede:  'Large enterprises run on dozens of systems and hundreds of workflows. MetaxuOne unifies operations across ERP, CRM, HCM, ITSM, and custom apps — so the whole organisation sees one truth.',
    stats: [
      { k: 'Surface area',  v: 'ERP · CRM · HCM · ITSM · Custom' },
      { k: 'Time-to-value', v: 'First outcome in weeks' },
      { k: 'Outcomes',      v: 'Automation rate · cycle time · cost-to-serve' },
    ],
    pillars: {
      observability: { body: 'Connect every system that runs the business into one operational graph — including the legacy and custom apps that other platforms can\'t see.', points: ['100+ system connectors','Cross-domain process lineage','Single KPI surface'] },
      selfHealing:   { body: 'Cross-system playbooks that route around bottlenecks before they show up in the close, the close, or the customer call.', points: ['Cross-system remediation','Bottleneck auto-routing','Closed-loop continuous improvement'] },
      agents:        { body: 'Agents grounded in the live process graph so they reason on real state — not stale dashboards or hallucinated workflows.', points: ['Context-grounded reasoning','Multi-step workflow orchestration','Governance & audit by design'] },
    },
    useCases: [
      { icon: LineChart, title: 'Finance close acceleration', text: 'Agents reconcile and investigate the routine — humans handle exceptions.' },
      { icon: Activity,  title: 'IT incident MTTR',           text: 'Cross-system root cause in seconds, not hours.' },
      { icon: Workflow,  title: 'Procure-to-pay automation',   text: 'End-to-end automation across ERP, supplier portals, and inbox.' },
    ],
  },

  pharma: {
    slug: 'pharma', icon: FlaskConical, image: IMG.pharma,
    title: ['GxP-aware operations.', 'AI you can defend.'],
    lede:  'In regulated pharma operations, every decision must be observable, validated, and defensible. MetaxuOne brings observability, self-healing, and AI agents into the GxP-aware operating fabric.',
    stats: [
      { k: 'Regulator-ready', v: 'GxP · 21 CFR Part 11 · GDPR' },
      { k: 'Validation',      v: 'Computer System Validation aware' },
      { k: 'Evidence',        v: 'Auto-collected · auditor-ready' },
    ],
    pillars: {
      observability: { body: 'Observable manufacturing and lab systems with full audit trail — every batch, every change, every signal captured for regulatory review.', points: ['Batch-level observability','Validated change management','Auditor-ready evidence'] },
      selfHealing:   { body: 'Self-healing within validated boundaries — automated remediation that stays within the lines a regulator drew.', points: ['Policy-bounded auto-remediation','Validated playbooks','Full action lineage'] },
      agents:        { body: 'Agents that operate inside compliant workflows — assisting investigators, deviation reviews, and quality operations without ever acting outside policy.', points: ['Deviation review co-pilot','Quality ops assistance','Audit-grade decision logs'] },
    },
    useCases: [
      { icon: AlertTriangle, title: 'Deviation management', text: 'Faster investigations with traceable reasoning.' },
      { icon: BookOpen,      title: 'CSV-aware change',     text: 'Validated change without slowing the line.' },
      { icon: Lock,          title: 'Audit readiness',       text: 'Continuous evidence for regulators and auditors.' },
    ],
  },

  manufacturing: {
    slug: 'manufacturing', icon: Factory, image: IMG.manufacturing,
    title: ['Shop floor to top floor —', 'one operating picture.'],
    lede:  'Modern manufacturing depends on OT-IT integration, predictive maintenance, and resilient production lines. MetaxuOne brings observability, self-healing, and AI agents to the systems that make the things.',
    stats: [
      { k: 'OT + IT',     v: 'Unified visibility' },
      { k: 'OEE uplift',  v: 'Measured monthly' },
      { k: 'Downtime',    v: 'Predicted, then prevented' },
    ],
    pillars: {
      observability: { body: 'Connect OT (MES, SCADA, historian) to IT (ERP, CMMS, quality systems) into one operational picture — surface line-level KPIs in real time.', points: ['OT + IT unified telemetry','Line-level KPIs','Predictive maintenance signals'] },
      selfHealing:   { body: 'Self-healing production workflows — detect deviations, reroute around bottlenecks, and trigger maintenance before downtime hits.', points: ['Predictive maintenance','Production rerouting','Bottleneck remediation'] },
      agents:        { body: 'AI agents support operators on the floor — surfacing root causes, suggesting interventions, and codifying tribal knowledge into reusable playbooks.', points: ['Operator co-pilot','Root cause assist','Knowledge → automation'] },
    },
    useCases: [
      { icon: Activity,  title: 'Predictive maintenance', text: 'Predict failures days before they happen — and prevent them.' },
      { icon: LineChart, title: 'OEE optimisation',       text: 'Lift OEE month over month against a baselined scorecard.' },
      { icon: Workflow,  title: 'Quality auto-triage',    text: 'AI-assisted quality investigations across MES + ERP signals.' },
    ],
  },

  government: {
    slug: 'government', icon: Shield, image: IMG.government,
    title: ['Sovereign, accountable,', 'autonomous.'],
    lede:  'Public-sector operations need to be sovereign-deployable, audit-ready, and citizen-centred. MetaxuOne brings observability, self-healing, and AI agents to citizen-services and back-office operations — within the boundaries the public expects.',
    stats: [
      { k: 'Deployment', v: 'On-prem · sovereign cloud · hybrid' },
      { k: 'Posture',    v: 'Audit-ready continuously' },
      { k: 'Outcomes',   v: 'Service levels for citizens' },
    ],
    pillars: {
      observability: { body: 'Observable citizen-service operations — from the portal to the back-office system — with full visibility into wait times, SLA breaches, and process bottlenecks.', points: ['Citizen-service SLAs','Cross-department visibility','Public-facing scorecards'] },
      selfHealing:   { body: 'Self-healing workflows that close service gaps without paging an operator — within strict policy and audit controls.', points: ['Policy-bounded remediation','Public-service uptime','Auditable actions'] },
      agents:        { body: 'AI agents support case workers and back-office operators on routine investigations — never acting autonomously where policy or law forbids it.', points: ['Case-worker co-pilot','Policy-bound autonomy','Full decision lineage'] },
    },
    useCases: [
      { icon: Workflow,      title: 'Citizen-service SLA', text: 'Hit and prove service levels for citizen-facing workflows.' },
      { icon: Lock,          title: 'Audit & accountability', text: 'Every operational action logged and explainable.' },
      { icon: AlertTriangle, title: 'Incident response',   text: 'Coordinated cross-department incident response.' },
    ],
  },
};

export const INDUSTRY_ORDER = ['bfsi','enterprise','pharma','manufacturing','government'];
export const INDUSTRY_LABELS = {
  bfsi: 'BFSI', enterprise: 'Enterprise', pharma: 'Pharma', manufacturing: 'Manufacturing', government: 'Government',
};
export { PILLAR };
